import React, { useState } from "react"
import { Modal, Input, Select, Typography } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import { useCreateProjectFolderMutation } from "features/Projects/api/useProjectsMutations"

interface NewFolderModalProps {
  open: boolean
  onClose: () => void
  existingFolders: string[]
  defaultParent?: string
}

const NewFolderModal: React.FC<NewFolderModalProps> = ({
  open,
  onClose,
  existingFolders,
  defaultParent,
}) => {
  const { Title } = Typography
  const createFolderMutation = useCreateProjectFolderMutation()

  const [parent, setParent] = useState<string | undefined>(defaultParent)
  const [name, setName] = useState<string>("")

  const reset = () => {
    setParent(defaultParent)
    setName("")
  }

  const handleSubmit = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      notify.error(
        "Folder name required",
        "Please enter a name for the folder."
      )
      return
    }
    if (trimmed.includes(".")) {
      notify.error(
        "Invalid folder name",
        "Folder names cannot contain '.'. Choose a parent folder instead."
      )
      return
    }
    const fullPath = parent ? `${parent}.${trimmed}` : trimmed
    try {
      await createFolderMutation.mutateAsync(fullPath)
      notify.success("Folder created", `Created folder '${fullPath}'.`)
      reset()
      onClose()
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error"
      notify.error("Error creating folder", message)
    }
  }

  const parentOptions = [
    { label: "(No parent)", value: "" },
    ...existingFolders.map((f) => ({ label: f, value: f })),
  ]

  return (
    <Modal
      open={open}
      centered
      footer={false}
      closable={false}
      destroyOnHidden
      onCancel={() => {
        reset()
        onClose()
      }}
    >
      <Title level={4} style={{ marginTop: "0.5em" }}>
        New Folder
      </Title>
      <div className="flex flex-col space-y-3 mt-2">
        <div>
          <label className="block mb-1 text-sm">Parent folder</label>
          <Select
            className="w-full"
            value={parent ?? ""}
            options={parentOptions}
            onChange={(v) => setParent(v || undefined)}
          />
        </div>
        <div>
          <label className="block mb-1 text-sm">Folder name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Trackers"
            onPressEnter={handleSubmit}
            autoFocus
          />
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <DataQButton
            type="secondary"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Cancel
          </DataQButton>
          <DataQButton
            type="primary"
            onClick={handleSubmit}
            isDisabled={createFolderMutation.isPending}
          >
            Create
          </DataQButton>
        </div>
      </div>
    </Modal>
  )
}

export default NewFolderModal
